import React, { useState, useRef } from 'react';
import { UploadCloud } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://martechai-backend.onrender.com';

// --- Reusable Button (for Image Upload) ---
const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false, ...props }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-700 text-slate-200 hover:bg-slate-600',
    };
    return (
        <button type="button" onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`} {...props}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};


// --- Modular & Reusable Form Field Components ---
const FormField = ({ label, children }) => (
    <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
        {children}
    </div>
);

const TextInput = ({ value, onChange, placeholder, type = "text" }) => (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-brand-500 outline-none"/>
);

const TextArea = ({ value, onChange, placeholder, rows = 4 }) => (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg resize-y text-white focus:ring-2 focus:ring-brand-500 outline-none"/>
);

const ImageUpload = ({ label, onFileSelect, value }) => {
    const fileInputRef = useRef(null);
    const fullImageUrl = value ? `${API_BASE_URL}/api/foundry/content-assets/${value}` : null;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            onFileSelect(file);
        }
    };

    return (
        <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
            <div className="flex items-center gap-4">
                {value && (
                    <div className="w-24 h-24 rounded-lg bg-slate-800 flex-shrink-0">
                        <img src={fullImageUrl} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                    </div>
                )}
                <div className="flex-grow">
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                    <Button onClick={() => fileInputRef.current.click()} variant="secondary" icon={UploadCloud}>
                        Upload Image
                    </Button>
                    {value && <p className="text-xs text-slate-500 mt-2">Filename: {value}</p>}
                </div>
            </div>
        </div>
    );
};

// --- Channel Configuration ---
export const channelConfig = {
    'wordpress-blog': {
        title: 'WordPress Blog',
        fields: [
            { id: 'title', label: 'Title', type: 'text', placeholder: 'Your blog post title' },
            { id: 'featuredImage', label: 'Featured Image', type: 'image' },
            { id: 'seoTitle', label: 'SEO Title', type: 'text', placeholder: 'SEO-friendly title' },
            { id: 'seoDescription', label: 'SEO Description', type: 'textarea', placeholder: 'Meta description', rows: 3 },
            { id: 'content', label: 'Main Blog Content', type: 'textarea', placeholder: 'Write your article here...', rows: 12 },
        ]
    },
    'emails': {
        title: 'Email',
        fields: [
            { id: 'title', label: 'Internal Email Name', type: 'text', placeholder: 'e.g., Q3 Product Launch' },
            { id: 'senderName', label: 'Sender Name', type: 'text', placeholder: 'e.g., Alex from Acme Inc.' },
            { id: 'senderEmail', label: 'Sender Email', type: 'email', placeholder: 'e.g., alex@acme.com' },
            { id: 'subject', label: 'Subject Line', type: 'text', placeholder: 'Your subject line here' },
            { id: 'body', label: 'Email Body (Markdown supported)', type: 'textarea', placeholder: 'Compose your email...', rows: 8 },
            { id: 'ctaUrl', label: 'Primary CTA (URL)', type: 'url', placeholder: 'https://example.com/learn-more' },
        ]
    },
    'webinars': {
        title: 'Webinar',
        fields: [
            { id: 'title', label: 'Webinar Title', type: 'text', placeholder: 'e.g., The Future of AI' },
            { id: 'description', label: 'Description / Abstract', type: 'textarea', placeholder: 'Briefly describe the webinar...', rows: 5 },
            { id: 'speakers', label: 'Speakers (one per line)', type: 'textarea', placeholder: 'Jane Doe, CEO\nJohn Smith, CTO', rows: 3 },
        ]
    },
    'google-ad': {
        title: 'Google Ad',
        fields: [
            { id: 'title', label: 'Ad Group Name', type: 'text', placeholder: 'e.g., Brand Campaign - US' },
            { id: 'headlines', label: 'Headlines (one per line, max 30 chars)', type: 'textarea', placeholder: 'Up to 15 headlines...\nEach on a new line...', rows: 6 },
            { id: 'descriptions', label: 'Descriptions (one per line, max 90 chars)', type: 'textarea', placeholder: 'Up to 4 descriptions...\nEach on a new line...', rows: 4 },
        ]
    },
    'linkedin-ad': {
        title: 'LinkedIn Ad',
        fields: [
            { id: 'title', label: 'Ad Name', type: 'text', placeholder: 'e.g., Ebook Promotion Ad' },
            { id: 'primaryText', label: 'Primary Text', type: 'textarea', placeholder: 'Ad copy...', rows: 5 },
            { id: 'headline', label: 'Headline', type: 'text', placeholder: 'A catchy headline' },
            { id: 'media', label: 'Image/Video', type: 'image' },
            { id: 'websiteUrl', label: 'Website URL', type: 'url', placeholder: 'https://example.com' },
        ]
    },
};

// --- Dynamic Form Renderer ---
export const DynamicCreatorForm = ({ channel, formData, setFormData, handleFileUpload }) => {
    const config = channelConfig[channel];
    if (!config) {
        return <div className="p-4 text-slate-400">Please select a content type from the left sidebar.</div>;
    }

    const handleChange = (fieldId, value) => {
        setFormData(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleImageSelected = async (fieldId, file) => {
        const filename = await handleFileUpload(file);
        if (filename) {
            handleChange(fieldId, filename);
        }
    };
    
    return (
        <div className="space-y-6">
            {config.fields.map(field => (
                <FormField key={field.id} label={field.label}>
                    {['text', 'email', 'url', 'date', 'time'].includes(field.type) ? 
                        <TextInput type={field.type} value={formData[field.id] || ''} onChange={e => handleChange(field.id, e.target.value)} placeholder={field.placeholder} />
                        : field.type === 'textarea' ?
                        <TextArea value={formData[field.id] || ''} onChange={e => handleChange(field.id, e.target.value)} placeholder={field.placeholder} rows={field.rows} />
                        : field.type === 'image' ?
                        <ImageUpload value={formData[field.id] || null} onFileSelect={(file) => handleImageSelected(field.id, file)} />
                        : null
                    }
                </FormField>
            ))}
        </div>
    );
};